import { useRef, useState } from 'react';
import { useSalePrefix } from '../lib/listingPaths';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { useMutation } from '../lib/useMutation';
import { shortDate } from '../lib/format';
import {
  Alert, Button, Card, ErrorBlock, Field, LoadingBlock, Select, TextInput,
} from '../components/ui';

/**
 * Asking NyumbaSuite to check that you may sell this property.
 *
 * The page has to do two things that pull against each other: get enough
 * evidence to make a real check possible, and be honest that the result is
 * modest. So it asks for documents about the PROPERTY, tells the seller in
 * as many words that we do not want their ID, and says what a buyer will
 * actually be told at the end of it — which is less than sellers tend to
 * assume "verified" means.
 */

interface Doc {
  id: string; kind: string; label: string | null; mime_type: string;
  size_bytes: number; created_at: string;
}

interface Capacity { code: string; label: string; asks: string }

interface Info {
  listing: {
    title: string; status: string;
    verifiedAt: string | null; verifiedCapacity: string | null; titleDeedStatus: string;
  };
  capacities: Capacity[];
  open: {
    id: string; state: string; capacity: string; declaration: string | null;
    submitted_at: string; documents: Doc[];
  } | null;
  history: {
    state: string; capacity: string; submitted_at: string;
    decided_at: string | null; decision_note: string | null;
  }[];
  limits: { maxDocuments: number; maxBytes: number };
}

const KIND_LABEL: Record<string, string> = {
  title_copy: 'A copy of the title',
  mandate_letter: 'A letter from the owner instructing you',
  development_approval: 'An approval for the development',
  search_certificate: 'A search certificate',
  other: 'Something else',
};

export function SaleVerification() {
  const salePrefix = useSalePrefix();
  const { id } = useParams<{ id: string }>();
  const view = useApi<Info>(`/api/v1/sale-listings/${id}/verification`);
  const act = useMutation<any>();
  const fileRef = useRef<HTMLInputElement>(null);

  const [capacity, setCapacity] = useState('');
  const [declaration, setDeclaration] = useState('');
  const [kind, setKind] = useState('title_copy');
  const [label, setLabel] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (view.loading) return <div className="page"><Card><LoadingBlock /></Card></div>;
  if (view.error) {
    return <div className="page"><Card><ErrorBlock error={view.error} onRetry={view.reload} /></Card></div>;
  }

  const d = view.data!;
  const chosen = d.capacities.find((c) => c.code === (d.open?.capacity ?? capacity));

  const ask = async () => {
    setError(null); setNotice(null);
    try {
      await act.run(`/api/v1/sale-listings/${id}/verification`, {
        body: { capacity, declaration: declaration.trim() },
      });
      setNotice('Asked. Attach what you have below — we cannot check anything without it.');
      view.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not send.');
    }
  };

  const upload = async (file: File) => {
    setError(null); setNotice(null);
    if (file.size > d.limits.maxBytes) {
      setError('That file is over 5 MB. A photograph of the page is usually enough.');
      return;
    }
    const content = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
      r.onerror = () => reject(new Error('That file could not be read.'));
      r.readAsDataURL(file);
    });
    try {
      await act.run(`/api/v1/sale-listings/${id}/verification/documents`, {
        body: { kind, label: label.trim() || undefined, mimeType: file.type, content },
      });
      setLabel('');
      if (fileRef.current) fileRef.current.value = '';
      setNotice('Attached.');
      view.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not upload.');
    }
  };

  const removeDoc = async (docId: string) => {
    setError(null);
    await act.run(`/api/v1/sale-verification-documents/${docId}`, { method: 'DELETE' });
    view.reload();
  };

  const withdraw = async () => {
    setError(null);
    await act.run(`/api/v1/sale-listings/${id}/verification`, { method: 'DELETE' });
    setNotice('Withdrawn.');
    view.reload();
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Get “{d.listing.title}” checked</h1>
          <p className="page-sub">
            Buyers are cautious, and rightly. A check on the seller is the one thing that
            makes an advert easier to believe.
          </p>
        </div>
        <Link to={`${salePrefix}/${id}`}>Back to the listing</Link>
      </div>

      {notice && <Alert tone="success">{notice}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      {d.listing.verifiedAt ? (
        <Card title="Checked">
          <p>
            We checked this on {shortDate(d.listing.verifiedAt)}. The advert now shows a
            buyer that we confirmed who is selling.
          </p>
          <p className="muted">
            {/* Said here as well as on the advert: a seller who believes this is a
                title guarantee will repeat that belief to a buyer. */}
            It says we checked who is selling — not that the title is good, and not that a
            buyer can skip their advocate’s search. If you change the address, tenure, land
            size or title status, the check comes off and you can ask again.
          </p>
        </Card>
      ) : d.open ? (
        <Card title="With us now">
          <p>
            Asked on {shortDate(d.open.submitted_at)}, as{' '}
            {d.capacities.find((c) => c.code === d.open!.capacity)?.label ?? d.open.capacity}.
          </p>
          {chosen && <p className="muted">What we need: {chosen.asks}</p>}

          <h3 className="card-title">What you have attached</h3>
          {d.open.documents.length === 0 ? (
            <Alert tone="warning">
              Nothing yet. We cannot check a property from a description alone — attach at
              least the title copy or the letter instructing you.
            </Alert>
          ) : (
            <ul className="plan-list">
              {d.open.documents.map((doc) => (
                <li key={doc.id}>
                  {KIND_LABEL[doc.kind] ?? doc.kind}{doc.label ? ` — ${doc.label}` : ''}
                  <span className="muted"> · {Math.round(doc.size_bytes / 1024)} KB</span>{' '}
                  <Button size="sm" variant="ghost" onClick={() => removeDoc(doc.id)}>Remove</Button>
                </li>
              ))}
            </ul>
          )}

          {d.open.documents.length < d.limits.maxDocuments && (
            <>
              <div className="grid grid-2">
                <Field label="What is this?" htmlFor="v-kind">
                  <Select id="v-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                    {Object.entries(KIND_LABEL).map(([code, text]) => (
                      <option key={code} value={code}>{text}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="A note, if it helps" htmlFor="v-label">
                  <TextInput id="v-label" value={label} onChange={(e) => setLabel(e.target.value)} />
                </Field>
              </div>
              <Field
                label="The file" htmlFor="v-file"
                hint="A PDF or a photograph, under 5 MB. Please do not send your ID — we do not keep identity documents."
              >
                <input
                  id="v-file" ref={fileRef} type="file" className="input"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
                />
              </Field>
            </>
          )}

          <Button variant="secondary" onClick={withdraw} disabled={act.busy}>
            Withdraw the request
          </Button>
        </Card>
      ) : (
        <Card title="Ask us to check it">
          <p>
            Tell us what you are — the owner, an agent, a developer — and show us the
            paperwork for the property. A person here reads it and, if it holds up, the
            advert says we checked who is selling.
          </p>
          <p className="muted">
            We do not want your national ID or passport. What we check is the property and
            your standing to sell it; identity we confirm by speaking to you.
          </p>

          <Field label="You are" htmlFor="v-capacity">
            <Select id="v-capacity" value={capacity} onChange={(e) => setCapacity(e.target.value)}>
              <option value="">Choose…</option>
              {d.capacities.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </Select>
          </Field>
          {chosen && <p className="muted">What we will ask for: {chosen.asks}</p>}

          <Field
            label="In your own words" htmlFor="v-decl"
            hint="How you came to be selling this property. A few sentences is plenty."
          >
            <textarea
              id="v-decl" className="input" rows={4} value={declaration}
              onChange={(e) => setDeclaration(e.target.value)}
            />
          </Field>

          <Button
            onClick={ask}
            disabled={act.busy || !capacity || declaration.trim().length < 10}
          >
            {act.busy ? 'Sending…' : 'Ask us to check it'}
          </Button>
        </Card>
      )}

      {d.history.length > 0 && (
        <Card title="Earlier requests" bodyClassName="card-pad-0">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Asked</th><th>As</th><th>Outcome</th><th>What was said</th></tr>
              </thead>
              <tbody>
                {d.history.map((h, i) => (
                  <tr key={i}>
                    <td>{shortDate(h.submitted_at)}</td>
                    <td>{d.capacities.find((c) => c.code === h.capacity)?.label ?? h.capacity}</td>
                    <td>{h.state === 'refused' ? 'Not enough' : 'Withdrawn'}</td>
                    <td className="muted">{h.decision_note ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
